import { useRef, useState, useEffect, useCallback } from 'react';
import Image from 'next/legacy/image';
import { usePlayerPicker } from './player-picker-provider';
import { isBelowTeamLimit } from './team-helpers';
import { POSITION_BY_ID } from './types';
import type { TeamMember } from './types';
import PitchPlayer, { PlayerInfo } from './pitch-player';
import { PlayerCircle, AddPlusOverlay } from './player-circle';
import InputField from '@components/atoms/input';
import {
  BodyRegular,
  BodyRegularBold,
  BodySmallRegular
} from '@components/atoms/typography';
import { BLANK_SHIRT_PATH } from '@lib/utils';
import type { Player } from '@api-service/players';

type PlayerPickerProps = {
  positionId: number;
  playerId: number;
  team: TeamMember[];
  setTeam: React.Dispatch<React.SetStateAction<TeamMember[]>>;
  onCancel: () => void;
  openUpwards?: boolean;
};

// Dropdown is 18rem (w-72) wide and must keep at least 1.5rem (ml-6) clear of
// the viewport edges.
const DROPDOWN_WIDTH = 288;
const EDGE_MARGIN = 24;

const PlayerPicker = ({
  positionId,
  playerId,
  team,
  setTeam,
  onCancel,
  openUpwards = false
}: PlayerPickerProps) => {
  const {
    players,
    isLoading,
    isFetching,
    activeSlotId,
    searchTerm,
    setSearchTerm,
    openSlot,
    closeSlot
  } = usePlayerPicker();

  const selectedIds = new Set(
    team
      .map(({ player }) => player?.id)
      .filter((id): id is string => id !== undefined)
  );
  const selectedPlayers = team
    .map(({ player }) => player)
    .filter((p): p is Player => p !== null);

  const isOpen = playerId === activeSlotId;

  // Position the dropdown relative to the viewport (so it escapes the pitch's
  // overflow clipping), horizontally centred on the trigger icon but clamped so
  // it never falls within EDGE_MARGIN of the screen edges.
  const triggerRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{
    left: number;
    top?: number;
    bottom?: number;
  } | null>(null);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = Math.min(DROPDOWN_WIDTH, window.innerWidth - EDGE_MARGIN * 2);
    const iconCentre = rect.left + rect.width / 2;
    const maxLeft = window.innerWidth - width - EDGE_MARGIN;
    const left = Math.max(
      EDGE_MARGIN,
      Math.min(iconCentre - width / 2, maxLeft)
    );
    setCoords(
      openUpwards
        ? { left, bottom: window.innerHeight - rect.top + 8 }
        : { left, top: rect.top + 65 }
    );
  }, [openUpwards]);

  useEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  const filteredPlayers = isOpen
    ? players
        .filter((p) => !selectedIds.has(p.id))
        .filter((p) => isBelowTeamLimit(p, selectedPlayers, 3))
    : [];

  const selectedPlayer = team[playerId].player;

  const handleSelectPlayer = (player: Player) => {
    const teamCopy = [...team];
    teamCopy[playerId] = { playerId, player };
    setTeam([...teamCopy]);
    closeSlot();
  };

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      {selectedPlayer ? (
        <PitchPlayer
          name={selectedPlayer.displayName}
          onCancel={onCancel}
          teamCode={selectedPlayer.team.shortName}
          price={`£${selectedPlayer.price.toFixed(1)}m`}
          imageUrl={selectedPlayer.imageUrl}
        />
      ) : (
        <div
          ref={triggerRef}
          data-cy={`player-picker-slot-${playerId}`}
          className="z-10 flex flex-col items-center pointer"
          onClick={() => {
            openSlot(playerId, POSITION_BY_ID[positionId]);
          }}
        >
          <PlayerCircle active={isOpen}>
            {!isOpen && <AddPlusOverlay />}
          </PlayerCircle>
          <PlayerInfo invisible />
        </div>
      )}
      {isOpen && coords && (
        <div
          style={{
            left: coords.left,
            ...(coords.top != null
              ? { top: coords.top }
              : { bottom: coords.bottom })
          }}
          className="fixed shadow-300 rounded-lg p-4 bg-white z-50 w-72 max-w-[calc(100vw-3rem)]"
        >
          <BodyRegularBold>SEARCH</BodyRegularBold>
          <InputField
            className="mb-6 w-full"
            name="player-search"
            value={searchTerm}
            placeholder="Player name or team..."
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm.length < 2 && !isLoading && (
            <BodyRegularBold className="mb-3">RECOMMENDATIONS</BodyRegularBold>
          )}
          <div className="h-[200px] overflow-y-scroll">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-success-800" />
              </div>
            ) : filteredPlayers.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <BodyRegular textColor="text-neutral-400">
                  No players found
                </BodyRegular>
              </div>
            ) : (
              <>
                {isFetching && !isLoading && (
                  <div className="flex justify-center mb-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-success-800" />
                  </div>
                )}
                {filteredPlayers.slice(0, 20).map((player) => (
                  <div
                    className="flex space-x-2 items-center p-2 mb-1 rounded-lg transition hover:bg-neutral-200 pointer"
                    key={player.id}
                    onClick={() => handleSelectPlayer(player)}
                  >
                    <Image
                      src={player.kitImageUrl ?? BLANK_SHIRT_PATH}
                      alt="Kit"
                      height={38}
                      width={35}
                    />
                    <div className="flex flex-col">
                      <BodyRegular>{player.displayName}</BodyRegular>
                      <div className="flex items-center">
                        <BodySmallRegular textColor="text-neutral-600">
                          {player.team.fullName}
                        </BodySmallRegular>
                        <div className="w-1 h-1 bg-neutral-400 rounded mx-1" />
                        <BodySmallRegular textColor="text-neutral-600">
                          {`£${player.price.toFixed(1)}`}m
                        </BodySmallRegular>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerPicker;
